import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import SortDirection, { type SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { data, tableConfig } from './content';
import type { Activity, ColumnsConfig, TransformedUser, User, UserState } from './content';

export type DemoServiceState = {
    userList: User[];
    activeUser: TransformedUser | null;
    selectedUserIds: string[];
    sortBy: ColumnsConfig['columnOrder'][number];
    sortDir: SortDirectionType;
    chunks: number;
    columns: ColumnsConfig;
    activityFilter: Activity[];
    stateFilter: UserState[];
    search: string;
    viewType: TableViewTogglesViewType;
    showConfirmDelete: boolean;
    showCreateAppointment: boolean;
    showCreateSingleAppointment: boolean;
    isPrintSidebar: boolean;
    isPrintTable: boolean;
};

export const defaultAppState: DemoServiceState = {
    userList: data,
    activeUser: null,
    selectedUserIds: [],
    sortBy: tableConfig.defaultSortKey,
    sortDir: SortDirection.ASCENDING,
    chunks: 1,
    columns: {
        columnOrder: tableConfig.defaultColumnOrder,
        hiddenColumns: tableConfig.defaultHiddenColumns,
        columnsDetails: tableConfig.defaultColumnsDetails,
    },
    activityFilter: [],
    stateFilter: [],
    search: '',
    viewType: TableViewToggles.VIEW_TYPE_TABLE,
    showConfirmDelete: false,
    showCreateAppointment: false,
    showCreateSingleAppointment: false,
    isPrintSidebar: false,
    isPrintTable: false,
};

export const demoServiceSlice = createSlice({
    name: 'demoService',
    initialState: defaultAppState,
    reducers: {
        deleteUsers: (state, action: PayloadAction<string[]>) => {
            const userIds = new Set(action.payload || []);
            state.userList = state.userList.filter(user => !userIds.has(user.itemId));
            state.activeUser = null;
        },
        selectedUserIds: (state, action: PayloadAction<string[]>) => {
            state.selectedUserIds = action.payload || [];
        },
        showConfirmDelete: (state, action: PayloadAction<boolean>) => {
            state.showConfirmDelete = Boolean(action.payload);
        },
        showCreateAppointment: (state, action: PayloadAction<boolean>) => {
            state.showCreateAppointment = Boolean(action.payload);
        },
        showCreateSingleAppointment: (state, action: PayloadAction<boolean>) => {
            state.showCreateSingleAppointment = Boolean(action.payload);
        },
        activeUser: (state, action: PayloadAction<TransformedUser | null>) => {
            state.activeUser = action.payload ?? null;
        },
        printSidebar: (state, action: PayloadAction<boolean>) => {
            state.isPrintSidebar = Boolean(action.payload);
        },
        printTable: (state, action: PayloadAction<boolean>) => {
            state.isPrintTable = Boolean(action.payload);
        },
        setSearch: (state, action: PayloadAction<string>) => {
            state.search = action.payload;
        },
        setSort: (
            state,
            action: PayloadAction<{ sortBy: ColumnsConfig['columnOrder'][number]; sortDir: SortDirectionType }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortDir = action.payload.sortDir;
        },
        setChunks: (state, action: PayloadAction<number>) => {
            state.chunks = action.payload;
        },
        setViewType: (state, action: PayloadAction<TableViewTogglesViewType>) => {
            state.viewType = action.payload;
        },
        setColumns: (state, action: PayloadAction<ColumnsConfig>) => {
            state.columns = action.payload;
        },
        setActivityFilter: (state, action: PayloadAction<Activity[]>) => {
            state.activityFilter = action.payload;
        },
        setStateFilter: (state, action: PayloadAction<UserState[]>) => {
            state.stateFilter = action.payload;
        },
    },
});

export const actions = demoServiceSlice.actions;

export default demoServiceSlice.reducer;
